"use client";

import { useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import type { Branch, ProductUsageMethod } from "@/types";
import { getDeliveryLabel } from "@/lib/utils/delivery-label";

interface BusinessInfoSectionProps {
  branches: Branch[];
  usageMethods: ProductUsageMethod[];
  onViewDetails?: () => void;
  /** Partner's ISO-2 country code. Used to render `delivery` as country-scoped label. */
  partnerCountryCode?: string | null;
}

export default function BusinessInfoSection({
  branches,
  usageMethods,
  onViewDetails,
  partnerCountryCode,
}: BusinessInfoSectionProps) {
  const t = useTranslations("product");
  const tUsage = useTranslations("product.usageMethods");
  const locale = useLocale();

  const getMethodLabel = (methodType: ProductUsageMethod["method_type"]) => {
    if (methodType === "delivery") {
      return getDeliveryLabel(partnerCountryCode, locale, tUsage, {
        fallbackKey: "delivery",
        templateKey: "deliveryTemplate",
      });
    }
    return t(`usageMethods.${methodType}`);
  };

  // 활성화된 이용 방식만 필터링
  const activeMethods = useMemo(() => {
    return usageMethods
      .filter((m) => m.enabled)
      .sort((a, b) => {
        const order: Record<string, number> = {
          pickup: 0,
          shipping: 1,
          delivery: 2,
        };
        return (order[a.method_type] ?? 99) - (order[b.method_type] ?? 99);
      });
  }, [usageMethods]);

  // 최대 3개까지만 표시
  const maxVisible = 3;
  const visibleBranches = branches.slice(0, maxVisible);
  const hiddenBranchCount = Math.max(0, branches.length - maxVisible);

  return (
    <div className="pt-2 pb-4">
      {/* 헤더: 영업 정보 + 전체보기 */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t("businessInfo.title")}</h3>
        <button
          type="button"
          onClick={onViewDetails}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          {t("businessInfo.viewAll")}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* 지점 행 - 최대 3개 표시, 넘치면 +N 표시 */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-gray-500 w-16 shrink-0">
          {t("businessInfo.branches")}
        </span>
        <div className="flex gap-2 flex-wrap">
          {visibleBranches.map((branch) => (
            <Badge
              key={branch.id}
              variant="secondary"
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-default"
            >
              {branch.name}
            </Badge>
          ))}
          {hiddenBranchCount > 0 && (
            <Badge
              variant="secondary"
              className="bg-gray-100 text-gray-500 cursor-pointer hover:bg-gray-200"
              onClick={onViewDetails}
            >
              +{hiddenBranchCount}
            </Badge>
          )}
        </div>
      </div>

      {/* 방식 행 */}
      {activeMethods.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 w-16 shrink-0">
            {t("businessInfo.methods")}
          </span>
          <div className="flex flex-wrap gap-2">
            {activeMethods.map((method) => (
              <Badge
                key={method.id}
                variant="secondary"
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-default"
              >
                {getMethodLabel(method.method_type)}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
